/**
 * Shared API service for Wideby API calls and postMessage communication
 */

/**
 * The API this app talks to, by the host it is served from: app.wideby.com uses the prod API,
 * devapp the dev API, anything else (local development) REACT_APP_API_SERVER_URL or localhost.
 */
const apiBaseUrl = () => {
  const host = typeof window === 'undefined' ? '' : window.location.hostname;
  if (host === 'app.wideby.com') return 'https://api.wideby.com';
  if (host === 'devapp.wideby.com') return 'https://devapi.wideby.com';
  return process.env.REACT_APP_API_SERVER_URL || 'http://localhost:3010';
};

export const API_BASE_URL = apiBaseUrl();

/** func_status 1 is OlaCore's Ok; anything else is a refusal the API still answers with HTTP 200. */
const FUNC_STATUS_OK = 1;

/** The body when the API did what was asked, otherwise an error naming the endpoint and OlaCore's reason. */
const checkedBody = (body, endpoint) => {
  if (body && typeof body === 'object' && body.func_status !== undefined && body.func_status !== FUNC_STATUS_OK) {
    throw new Error(`${endpoint} failed: ${body.func_msg || `status ${body.func_status}`}`);
  }
  return body;
};

/**
 * Call the getUserString API and send result to parent window
 * @param {string} accessToken - Auth0 access token
 * @param {boolean} isAuthenticated - Whether user is authenticated
 */
export const callUserStringApi = async (accessToken, isAuthenticated) => {
  console.log('Calling API...');
  
  // If user is not authenticated, send status message immediately
  if (!isAuthenticated) {
    console.log('User not authenticated, sending logout status');
    sendPostMessage({
      userString: '',
      apiResponse: null,
      error: 'User not authenticated',
      status: 'logged_out'
    });
    return;
  }
  
  try {
    // Call API to get userString with authentication
    const response = await fetch(`${API_BASE_URL}/kuzfit/getUserString`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'ngrok-skip-browser-warning': 'true'
      }
    });

    console.log('API response status:', response.status);
    
    if (response.ok) {
      const responseText = await response.text();
      console.log('API response text:', responseText);
      
      // Send to parent WordPress site via postMessage
      sendPostMessage({
        userString: responseText,
        apiResponse: responseText,
        status: 'authenticated'
      });
    } else {
      console.error('API call failed:', response.status, response.statusText);
      // Send postMessage with empty string when API fails
      sendPostMessage({
        userString: '',
        apiResponse: null,
        error: `API failed: ${response.status}`,
        status: 'api_error'
      });
    }
  } catch (error) {
    console.error('Error calling API:', error);
    // Send postMessage with empty string when authentication or other errors occur
    sendPostMessage({
      userString: '',
      apiResponse: null,
      error: error.message,
      status: 'error'
    });
  }
};

/**
 * Send postMessage to parent window
 * @param {Object} data - Data to send
 */
export const sendPostMessage = (data) => {
  if (window.parent) {
    const message = {
      type: 'USER_STRING_UPDATE',
      ...data
    };
    
    console.log('Sending postMessage to parent window:', message);
    window.parent.postMessage(message, '*');
  } else {
    console.log('No parent window found for postMessage');
  }
};

/**
 * Set up polling for the API
 * @param {Function} apiCallFunction - Function to call the API
 * @param {number} intervalMs - Polling interval in milliseconds
 * @returns {Object} Object with start and stop methods
 */
export const createApiPoller = (apiCallFunction, intervalMs = 60000) => {
  let intervalRef = null;
  
  const start = () => {
    if (intervalRef) {
      console.log('Polling already started');
      return;
    }
    
    console.log(`Starting API polling every ${intervalMs / 1000} seconds`);
    intervalRef = setInterval(() => {
      console.log('Polling API...');
      apiCallFunction();
    }, intervalMs);
  };
  
  const stop = () => {
    if (intervalRef) {
      clearInterval(intervalRef);
      intervalRef = null;
      console.log('Polling stopped');
    }
  };
  
  return { start, stop };
};

/**
 * List all organisations for the current user
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response (DCR_OrgCompleteList)
 */
export const listUserOrganisations = async (accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/api/Read_All_User_Organisations`, {
    method: 'POST',
    headers,
    body: JSON.stringify({})
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Read_All_User_Organisations failed: ${response.status} ${text}`);
  }

  return checkedBody(await response.json(), 'Read_All_User_Organisations');
};

/**
 * The signed-in user's own Wideby profile, for their userId
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response (DCR_ContactComplete)
 */
export const readSelfProfile = async (accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/api/Read_Self_Profile`, {
    method: 'POST',
    headers,
    body: JSON.stringify({})
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Read_Self_Profile failed: ${response.status} ${text}`);
  }

  return checkedBody(await response.json(), 'Read_Self_Profile');
};

/**
 * LEGACY: Fetch all organisations from KuzFit API (deprecated - use listUserOrganisations)
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response
 */
export const fetchAllOrganisations = async (accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/kuzfit/organisationReadAll`, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`organisationReadAll failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * Create a new organisation
 * @param {Object} payload - Body matching DC_Create_Org schema
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response (DCR_Added)
 */
export const createOrganisation = async (payload, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/api/Create_Organisation`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Create_Organisation failed: ${response.status} ${text}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json') || contentType.includes('text/json')) {
    return checkedBody(await response.json(), 'Create_Organisation');
  }
  return response.text();
};

/**
 * Deals near a point, nearest first. Works signed in or not; signed in, each deal says whether
 * the user has unlocked it.
 * @param {{latitude: number, longitude: number, radiusInMeters: number}} where
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response ({ offers: [...] })
 */
export const readOffersNear = async ({ latitude, longitude, radiusInMeters }, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/api/Read_Offers_Near`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ latitude, longitude, radiusInMeters })
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Read_Offers_Near failed: ${response.status} ${text}`);
  }

  return checkedBody(await response.json(), 'Read_Offers_Near');
};

/**
 * Get a single organisation by ID
 * @param {string|number} orgId - Organisation ID
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response (DCR_OrgComplete)
 */
export const getOrganisation = async (orgId, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/api/Read_Organisation`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgId })
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Read_Organisation failed: ${response.status} ${text}`);
  }

  return checkedBody(await response.json(), 'Read_Organisation');
};

/**
 * Update an existing organisation
 * @param {Object} payload - Body matching DC_Update_Org schema
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response (DCR_Update)
 */
export const updateOrganisation = async (payload, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/api/Update_Organisation`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Update_Organisation failed: ${response.status} ${text}`);
  }

  return checkedBody(await response.json(), 'Update_Organisation');
};

/**
 * Delete an organisation
 * @param {string|number} orgId - Organisation ID
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response (DCR_Delete)
 */
export const deleteOrganisation = async (orgId, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/api/Delete_Organisation`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgId })
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Delete_Organisation failed: ${response.status} ${text}`);
  }

  return checkedBody(await response.json(), 'Delete_Organisation');
};

/**
 * Add a member to an organisation
 * @param {string|number} orgId - Organisation ID
 * @param {Object} memberData - { emailAddress, firstName, lastName }
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response (DCR_Added)
 */
export const addOrganisationMember = async (orgId, { emailAddress, firstName, lastName }, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/api/Create_Member`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgId, emailAddress, firstName, lastName })
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Create_Member failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * Remove a member from an organisation
 * @param {string|number} orgId - Organisation ID
 * @param {string|number} memberId - Member ID
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response (DCR_Delete)
 */
export const removeOrganisationMember = async (orgId, memberId, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/api/Delete_Member`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgId, memberId })
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Delete_Member failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * Assign a role to an organisation member
 * @param {string|number} orgId - Organisation ID
 * @param {string|number} memberId - Member ID
 * @param {string|number} roleId - Role ID
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response (DCR_Added)
 */
export const assignMemberRole = async (orgId, memberId, roleId, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/api/Create_Role_Member_Mapping`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgId, memberId, roleId })
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Create_Role_Member_Mapping failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * Remove a role from an organisation member
 * @param {string|number} orgId - Organisation ID
 * @param {string|number} memberId - Member ID
 * @param {string|number} roleId - Role ID
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response (DCR_Delete)
 */
export const removeMemberRole = async (orgId, memberId, roleId, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/api/Delete_Member_Role_Mapping`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ orgId, memberId, roleId })
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Delete_Member_Role_Mapping failed: ${response.status} ${text}`);
  }

  return response.json();
};
